import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { Group, Button, Text, PinInput, Stack, Anchor, Alert, Box, Center } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { verifyEmailWithCode, VerificationResponse, resendVerificationCode } from "../../api/Auth";

interface VerificationParams {
    username: string;
    method: string;
    destination?: string;
}

const VerificationCode: React.FC = () => {
    const [pinInputError, setPinInputError] = useState(false);
    const [verificationParams, setVerificationParams] = useState<VerificationParams | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [resendCountdown, setResendCountdown] = useState(30);
    const [isResending, setIsResending] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const form = useForm({
        initialValues: {
            verificationCode: '',
        },
        validate: {
            verificationCode: (val) => (val.length === 6 ? null : 'Invalid code'),
        },
    });

    // Parse URL parameters
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const username = searchParams.get('username');
        const method = searchParams.get('method');
        const destination = searchParams.get('destination');

        if (!username || !method) {
            console.error('Missing required verification parameters');
            navigate('/login');
            return;
        }

        setVerificationParams({
            username,
            method,
            destination: destination || undefined
        });
    }, [location, navigate]);

    // Handle countdown timer
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (resendCountdown > 0) {
            timer = setInterval(() => {
                setResendCountdown(prev => prev - 1);
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [resendCountdown]);

    const handleResendCode = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!verificationParams || resendCountdown > 0 || isResending) return;

        setIsResending(true);
        try {
            const response = await resendVerificationCode(verificationParams.username);
            if (response.success) {
                setErrorMessage(null);
                setResendCountdown(30);
            } else {
                setErrorMessage(response.error || 'Failed to resend verification code');
            }
        } catch (error) {
            setErrorMessage('Failed to resend verification code. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    if (!verificationParams) {
        return null;
    }

    const destinationDisplay = verificationParams.destination;
    const methodDisplay = verificationParams.method === 'email' ? 'email address' : 'phone number';

    const handleVerification = async (username: string, code: string, onCodeError: (code: boolean) => void) => {
        setSubmitting(true);
        setErrorMessage(null);
        try {
            const response: VerificationResponse = await verifyEmailWithCode(username, code);

            if (response.success) {
                const successParams = new URLSearchParams({
                    verified: 'true',
                    message: 'Your account has been verified successfully! Please sign in to continue.'
                });
                navigate(`/login?${successParams.toString()}`);
                return;
            }
            
            // Handle different error cases
            switch (response.errorCode) {
                case 'CodeMismatch':
                case 'ExpiredCode':
                case 'TooManyAttempts':
                    setErrorMessage(response.error || 'Verification failed. Please try again.');
                    form.setFieldValue('verificationCode', '');
                    break;
                    
                case 'UserNotFound':
                    navigate(`/login?${new URLSearchParams({
                        error: 'account_not_found',
                        message: 'Account not found. Please sign up first.'
                    }).toString()}`);
                    break;
                    
                case 'AlreadyVerified':
                    navigate(`/login?${new URLSearchParams({
                        exists: 'true',
                        message: 'This account is already verified. Please sign in.'
                    }).toString()}`);
                    break;
                    
                default:
                    setErrorMessage(response.error || 'An unexpected error occurred. Please try again.');
                    form.setFieldValue('verificationCode', '');
            }
        } catch (error) {
            console.error('Verification error:', error);
            setErrorMessage('An unexpected error occurred. Please try again later.');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <Text size="lg" fw={500}>
                Verify Your Account
            </Text>
            
            <Center>
                <Box w="80%" mt="md">
                    {errorMessage && (
                        <Alert 
                            color="red" 
                            title="Error" 
                            mb="xl"
                            styles={{
                                root: { width: '100%' },
                                message: { fontSize: '0.9rem' }
                            }}
                        >
                            {errorMessage}
                        </Alert>
                    )}
                </Box>
            </Center>

            <Text size="sm" color="dimmed" mt="md">
                Please enter the 6-digit code we sent to your {methodDisplay}: {destinationDisplay}
            </Text>

            <form onSubmit={form.onSubmit((values) => handleVerification(verificationParams.username, values.verificationCode, setPinInputError))}>
                <Stack align="center" mt="lg">
                    <PinInput
                        oneTimeCode={true}
                        error={pinInputError}
                        placeholder={""}
                        length={6}
                        value={form.values.verificationCode}
                        onChange={(value) => {
                            form.setFieldValue('verificationCode', value);
                            if (pinInputError) {
                                setPinInputError(false);
                                setErrorMessage(null);
                            }
                        }}
                    />
                    
                    <Group justify="space-between" mt="xl" style={{ width: '100%' }}>
                        <Text size="xs" c="dimmed">
                            Not getting a code?{' '}
                            <Anchor 
                                component="button" 
                                type="button"
                                onClick={handleResendCode}
                                disabled={resendCountdown > 0 || isResending}
                                style={{ 
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: resendCountdown > 0 ? 'default' : 'pointer'
                                }}
                            >
                                {resendCountdown > 0 
                                    ? `Resend in ${resendCountdown}s` 
                                    : isResending 
                                        ? 'Sending...' 
                                        : 'Resend'}
                            </Anchor>
                        </Text>
                        <Button 
                            type="submit" 
                            radius="xl" 
                            loading={submitting}
                            disabled={form.values.verificationCode.length !== 6}
                        >
                            {submitting ? 'Verifying...' : 'Verify'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </>
    );
}

export default VerificationCode;
